import { useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import React, { useState } from 'react'

import { Badge, Button, Modal } from 'slab'
import { getFormattedDate } from 'utils'
import {
  getGetAdminSidebarPromoCardsQueryKey,
  useDeleteAdminSidebarPromoCardsId,
  useGetAdminSidebarPromoCards,
} from 'utils/web_api'
import { type AdminSidebarPromoCard } from 'utils/web_api.schemas'

import SidebarPromoCardForm from './SidebarPromoCardForm'
import { type SidebarPromoCardTargetAudience } from './types'

const SidebarPromoCards: React.FC = () => {
  const [targetAudienceFilter, setTargetAudienceFilter] = useState<string>('')
  const [editingCard, setEditingCard] = useState<AdminSidebarPromoCard | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ id: string; title: string } | null>(null)

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useGetAdminSidebarPromoCards(
    targetAudienceFilter
      ? { target_audience: targetAudienceFilter as SidebarPromoCardTargetAudience }
      : undefined
  )

  const deleteMutation = useDeleteAdminSidebarPromoCardsId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAdminSidebarPromoCardsQueryKey() })
      },
    },
  })

  const handleEdit = (card: AdminSidebarPromoCard) => {
    setEditingCard(card)
    setIsCreating(false)
  }

  const handleCreate = () => {
    setEditingCard(null)
    setIsCreating(true)
  }

  const handleCloseForm = () => {
    setEditingCard(null)
    setIsCreating(false)
  }

  const handleDelete = (card: AdminSidebarPromoCard) => {
    setDeleteConfirmModal({ id: card.id, title: card.title })
  }

  const confirmDelete = async () => {
    if (deleteConfirmModal) {
      await deleteMutation.mutateAsync({ id: deleteConfirmModal.id })
      setDeleteConfirmModal(null)
    }
  }

  if (isCreating || editingCard) {
    return (
      <SidebarPromoCardForm
        card={editingCard ?? undefined}
        onClose={handleCloseForm}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: getGetAdminSidebarPromoCardsQueryKey() })
          handleCloseForm()
        }}
      />
    )
  }

  const cards = data?.data || []

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center'>
        <h1 className='text-2xl font-semibold text-gray-900'>
          Sidebar Promo Cards
        </h1>
        <Button onClick={handleCreate} variant='primary' leftIcon={Plus}>
          Create New Card
        </Button>
      </div>

      <div className='flex items-center space-x-2'>
        <label htmlFor='audience-filter' className='text-sm font-medium text-gray-700'>
          Filter by Audience:
        </label>
        <select
          id='audience-filter'
          value={targetAudienceFilter}
          onChange={(e) => setTargetAudienceFilter(e.target.value)}
          className='block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
        >
          <option value=''>All</option>
          <option value='employer'>Employer</option>
          <option value='member'>Member</option>
          <option value='partner'>Partner</option>
        </select>
      </div>

      {isLoading && <div>Loading...</div>}

      {!isLoading && !error && (
        <div className='bg-white shadow rounded-lg overflow-hidden'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Title
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Audience
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Expires At
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {cards.length === 0
                ? (
                  <tr>
                    <td colSpan={5} className='px-6 py-4 text-center text-sm text-gray-500'>
                      No cards found
                    </td>
                  </tr>
                  )
                : (
                    cards.map((card) => (
                      <tr key={card.id}>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                          {card.title}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {card.target_audience}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <Badge type={card.active ? 'success' : 'neutral'}>
                            {card.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {card.expires_at ? getFormattedDate(card.expires_at, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Never'}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                          <div className='flex space-x-2'>
                            <button
                              onClick={() => handleEdit(card)}
                              className='text-indigo-600 hover:text-indigo-900'
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(card)}
                              className='text-red-600 hover:text-red-900'
                              aria-label={`Delete ${card.title}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!deleteConfirmModal} onOpenChange={(open) => !open && setDeleteConfirmModal(null)}>
        <Modal.Body>
          <Modal.Title>Delete Sidebar Promo Card</Modal.Title>
          <Modal.Description>
            Are you sure you want to delete "{deleteConfirmModal?.title}"? This action cannot be undone.
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Button variant='secondary' onClick={() => setDeleteConfirmModal(null)}>
            Cancel
          </Modal.Button>
          <Modal.Button variant='primary' onClick={confirmDelete}>
            Delete
          </Modal.Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SidebarPromoCards
